import { Skeleton } from '@/components/ui/skeleton';
import { PendingRegion } from '@/shared/components/PendingRegion';
import {
  ADMIN_TILE_GRID,
  LIMITS_POLICY_GRID,
  LIMITS_TABLE_COLUMN_COUNT,
  PLACEHOLDER_ROW_COUNT,
  WORKSPACES_TABLE_COLUMN_COUNT,
} from '../adminLayout';

/**
 * The console's placeholders (spec 079 US2).
 *
 * Each takes its container from `adminLayout.ts` rather than restating it, so the box
 * cannot drift from the content it stands in for. None of them draws content — no text
 * runs, no icons, no values (FR-015). A placeholder is the shape of what is coming, and
 * a drawing of the thing would be a small lie that has to be maintained.
 *
 * Row counts are fixed and short. The true count is unknowable while pending, and
 * guessing short is the lesser harm: content growing downward into empty space disturbs
 * less than content pulling upward past the fold (research D7).
 */

/**
 * The Limits page while its reads land (spec 117): the three policy cards and the one
 * table beneath them, at the table's own column count.
 */
export function LimitsTableSkeleton() {
  return (
    <PendingRegion label="Loading limits" className="gap-6">
      <div className={LIMITS_POLICY_GRID}>
        {Array.from({ length: 3 }, (_, card) => (
          <Skeleton key={card} className="h-24" />
        ))}
      </div>
      <div className="border-border overflow-hidden rounded-lg border">
        <div className="border-border flex gap-4 border-b px-4 py-2">
          {Array.from({ length: LIMITS_TABLE_COLUMN_COUNT }, (_, column) => (
            <Skeleton key={column} className="h-4 flex-1" />
          ))}
        </div>
        {Array.from({ length: PLACEHOLDER_ROW_COUNT }, (_, row) => (
          <div key={row} className="flex gap-4 px-4 py-2.5">
            {Array.from({ length: LIMITS_TABLE_COLUMN_COUNT }, (_, column) => (
              <Skeleton key={column} className="h-4 flex-1" />
            ))}
          </div>
        ))}
      </div>
    </PendingRegion>
  );
}

/** Admin → Workspaces' table while the workspaces are read (spec 195 US2). */
export function WorkspacesTableSkeleton() {
  return (
    <PendingRegion label="Loading workspaces">
      <div className="border-border overflow-hidden rounded-md border">
        <div className="border-border flex gap-4 border-b px-3 py-2">
          {Array.from(
            { length: WORKSPACES_TABLE_COLUMN_COUNT },
            (_, column) => (
              <Skeleton key={column} className="h-4 flex-1" />
            ),
          )}
        </div>
        {Array.from({ length: PLACEHOLDER_ROW_COUNT }, (_, row) => (
          <div key={row} className="flex gap-4 px-3 py-2.5">
            {Array.from(
              { length: WORKSPACES_TABLE_COLUMN_COUNT },
              (_, column) => (
                <Skeleton key={column} className="h-4 flex-1" />
              ),
            )}
          </div>
        ))}
      </div>
    </PendingRegion>
  );
}

/** The organization overview's tiles and the table beneath them. */
export function AdminOverviewSkeleton() {
  return (
    <PendingRegion label="Loading usage" className="gap-6">
      <div className={ADMIN_TILE_GRID}>
        {Array.from({ length: 4 }, (_, tile) => (
          <Skeleton key={tile} className="h-24" />
        ))}
      </div>
      <Skeleton className="h-56 w-full" />
    </PendingRegion>
  );
}

/**
 * The general settings form.
 *
 * Exact rather than a guess, unlike the lists above: a settings form's fields are known
 * statically, so there is nothing to estimate and no reason to reserve a wrong number.
 */
export function AdminGeneralSkeleton() {
  return (
    <PendingRegion label="Loading organization" className="gap-4">
      <Skeleton className="h-4 w-24" />
      <Skeleton className="h-9 w-full max-w-sm" />
      <Skeleton className="h-9 w-20" />
    </PendingRegion>
  );
}

/** The console shell, before it knows who is asking. */
export function AdminShellSkeleton() {
  return (
    <PendingRegion
      label="Loading the console"
      className="gap-6 max-md:px-4 max-md:pt-4 p-6"
    >
      <Skeleton className="h-6 w-48" />
      <Skeleton className="min-h-0 flex-1" />
    </PendingRegion>
  );
}
